/**
 * F-506-FE-04: PreSessionSetup
 *
 * 3-step pre-session wizard at /session/new:
 *   Step 1 — Patient language: Dropdown of active platform languages,
 *             doctor's own primary language excluded, auto-select if one option.
 *   Step 2 — Note template: Filtered by doctor's specialization, auto-select if one,
 *             warning banner if none configured.
 *   Step 3 — Audio readiness: Advisory checklist using usePreSessionAudioCheck hook.
 *             NOT blocking — session can start regardless.
 *
 * "Start Session" enables after steps 1 and 2.
 * Calls POST /api/sessions on start.
 *
 * Design: Clinical checklist feel. Step wizard with clear progress.
 * Mobile-first. Each step is its own card.
 * RTL support via CSS logical properties.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { usePreSessionAudioCheck } from '@/features/session/hooks/usePreSessionAudioCheck';
import { api } from '@/lib/api';
import type { DoctorProfile } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlatformLanguage {
  code: string;
  name: string;
  isActive: boolean;
}

interface TemplateVersionSummary {
  id: string;
  templateName: string;
}

interface CreateSessionResponse {
  sessionId: string;
  qrToken: string;
  sixDigitCode: string;
  expiresIn: number;
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

function StepIndicator({ step, totalSteps }: { step: number; totalSteps: number }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-6" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={totalSteps} aria-label={`Step ${step} of ${totalSteps}`}>
      {Array.from({ length: totalSteps }).map((_, i) => {
        const stepNumber = i + 1;
        const isActive = stepNumber === step;
        const isCompleted = stepNumber < step;
        return (
          <div key={stepNumber} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-body font-semibold transition-all duration-200 ${
                isCompleted
                  ? 'bg-clinical-teal text-white'
                  : isActive
                  ? 'bg-clinical-charcoal text-white'
                  : 'bg-clinical-cream-dark text-clinical-text-muted'
              }`}
              aria-hidden="true"
            >
              {isCompleted ? (
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
                </svg>
              ) : (
                stepNumber
              )}
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`w-8 h-0.5 transition-colors duration-200 ${
                  isCompleted ? 'bg-clinical-teal' : 'bg-clinical-border'
                }`}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Patient language
// ---------------------------------------------------------------------------

interface Step1Props {
  doctorLanguageCode: string;
  value: string;
  onChange: (code: string) => void;
}

function Step1Language({ doctorLanguageCode, value, onChange }: Step1Props) {
  const { data, isLoading } = useQuery<{ languages: PlatformLanguage[] }>({
    queryKey: ['languages'],
    queryFn: () => api.get<{ languages: PlatformLanguage[] }>('/api/languages'),
  });

  const languages = (data?.languages ?? []).filter(
    (l) => l.isActive && l.code !== doctorLanguageCode,
  );

  // Auto-select if only one option
  useEffect(() => {
    if (languages.length === 1 && !value) {
      onChange(languages[0].code);
    }
  }, [languages, value, onChange]);

  return (
    <div className="clinical-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-clinical-charcoal flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802" />
          </svg>
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-clinical-charcoal">
            Patient language
          </h3>
          <p className="text-sm font-body text-clinical-text-muted mt-0.5">
            Select the language your patient speaks
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="skeleton h-11 w-full rounded-clinical" />
      ) : languages.length === 0 ? (
        <div
          role="alert"
          className="p-3 rounded-clinical bg-amber-50 border border-amber-200 text-sm font-body text-amber-700 flex items-center gap-2"
        >
          <svg className="w-4 h-4 flex-shrink-0 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
          </svg>
          No additional languages available. Contact your administrator.
        </div>
      ) : (
        <div>
          <label htmlFor="patient-language" className="form-label">
            Patient language
          </label>
          <select
            id="patient-language"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="form-input"
            required
          >
            <option value="" disabled>
              {languages.length === 1 ? languages[0].name : 'Select language'}
            </option>
            {languages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Note template
// ---------------------------------------------------------------------------

interface Step2Props {
  specializationId: string;
  value: string;
  onChange: (id: string) => void;
}

function Step2Template({ specializationId, value, onChange }: Step2Props) {
  const { data, isLoading } = useQuery<{ templateVersions: TemplateVersionSummary[] }>({
    queryKey: ['templates', specializationId],
    queryFn: () =>
      api.get<{ templateVersions: TemplateVersionSummary[] }>(
        `/api/templates?specializationId=${specializationId}&activeOnly=true`,
      ),
    enabled: Boolean(specializationId),
  });

  const templates = data?.templateVersions ?? [];

  // Auto-select if only one
  useEffect(() => {
    if (templates.length === 1 && !value) {
      onChange(templates[0].id);
    }
  }, [templates, value, onChange]);

  return (
    <div className="clinical-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-clinical-charcoal flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-clinical-charcoal">
            Note template
          </h3>
          <p className="text-sm font-body text-clinical-text-muted mt-0.5">
            Select the template for this consultation
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="skeleton h-11 w-full rounded-clinical" />
      ) : templates.length === 0 ? (
        <div
          role="alert"
          className="p-3 rounded-clinical bg-amber-50 border border-amber-200 text-sm font-body text-amber-700 flex items-start gap-2"
        >
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
          </svg>
          <span>
            No note templates configured for your specialization. Contact your administrator to
            set up templates before starting a session.
          </span>
        </div>
      ) : (
        <div>
          <label htmlFor="note-template" className="form-label">
            Template
          </label>
          <select
            id="note-template"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="form-input"
            required
          >
            <option value="" disabled>
              {templates.length === 1 ? templates[0].templateName : 'Select template'}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.templateName}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Audio readiness (advisory)
// ---------------------------------------------------------------------------

function Step3AudioCheck() {
  const { checkResults, isChecking, runChecks } = usePreSessionAudioCheck();

  return (
    <div className="clinical-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-clinical-charcoal flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
          </svg>
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-clinical-charcoal">
            Audio readiness
          </h3>
          <p className="text-sm font-body text-clinical-text-muted mt-0.5">
            Advisory checks — not required to proceed
          </p>
        </div>
      </div>

      <div
        className="p-3 rounded-clinical bg-clinical-cream-dark border border-clinical-border text-xs font-body text-clinical-text-muted mb-4"
        role="note"
      >
        These checks are informational. You can start a session even if checks fail.
      </div>

      {checkResults.length === 0 && !isChecking ? (
        <button
          onClick={() => void runChecks()}
          className="btn-secondary w-full"
        >
          Run audio checks
        </button>
      ) : (
        <div className="space-y-3" role="list" aria-label="Audio check results">
          {checkResults.map((result) => (
            <div
              key={result.name}
              role="listitem"
              className="flex items-start gap-3"
            >
              {result.passed ? (
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-safety-green" viewBox="0 0 16 16" fill="currentColor" aria-label="Passed" aria-hidden="false">
                  <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-label="Warning" aria-hidden="false">
                  <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
                </svg>
              )}
              <div>
                <p className="text-sm font-body font-semibold text-clinical-charcoal">
                  {result.name}
                </p>
                <p className="text-xs font-body text-clinical-text-muted mt-0.5">
                  {result.message}
                </p>
              </div>
            </div>
          ))}

          {isChecking && (
            <div className="flex items-center gap-2 text-sm font-body text-clinical-text-muted">
              <span className="w-4 h-4 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              Checking...
            </div>
          )}

          {!isChecking && checkResults.length > 0 && (
            <button
              onClick={() => void runChecks()}
              className="text-xs font-body text-clinical-teal hover:text-clinical-teal-dark transition-colors mt-2"
            >
              Re-run checks
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PreSessionSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const doctor = user?.role === 'doctor' ? user : null;

  const [currentStep, setCurrentStep] = useState(1);
  const [patientLanguageCode, setPatientLanguageCode] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [safetyGateEnabled, setSafetyGateEnabled] = useState(false);

  const isStep1Complete = patientLanguageCode.length > 0;
  const isStep2Complete = templateVersionId.length > 0;
  const canStartSession = isStep1Complete && isStep2Complete;

  // ---------------------------------------------------------------------------
  // Create session mutation
  // ---------------------------------------------------------------------------
  const createSessionMutation = useMutation({
    mutationFn: () =>
      api.post<CreateSessionResponse>('/api/sessions', {
        patientLanguageCode,
        templateVersionId,
        safetyGateEnabled,
      }),
    onSuccess: (data) => {
      sessionStorage.setItem(`session_qr_${data.sessionId}`, JSON.stringify({
        qrToken: data.qrToken,
        sixDigitCode: data.sixDigitCode,
        expiresIn: data.expiresIn,
      }));
      navigate(`/session/${data.sessionId}`);
    },
    onError: (err: unknown) => {
      const e = err as Error;
      setCreateError(e.message || 'Failed to create session. Please try again.');
    },
  });

  function handleStartSession() {
    if (!canStartSession || createSessionMutation.isPending) return;
    setCreateError(null);
    createSessionMutation.mutate();
  }

  // Navigate step forward only if current step is valid
  function handleNextStep() {
    if (currentStep === 1 && isStep1Complete) setCurrentStep(2);
    else if (currentStep === 2 && isStep2Complete) setCurrentStep(3);
  }

  function handlePrevStep() {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal">
          New Session Setup
        </h1>
        <p className="text-sm font-body text-clinical-text-muted mt-1">
          Complete the steps below before starting the consultation
        </p>
      </div>

      {/* Step indicator */}
      <StepIndicator step={currentStep} totalSteps={3} />

      {/* Step content */}
      <div className="space-y-4">
        {currentStep === 1 && (
          <>
            <Step1Language
              doctorLanguageCode={doctor?.primaryLanguageCode ?? ''}
              value={patientLanguageCode}
              onChange={setPatientLanguageCode}
            />
            <button
              onClick={handleNextStep}
              disabled={!isStep1Complete}
              className="btn-primary w-full"
            >
              Next
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Step2Template
              specializationId={doctor?.specializationId ?? ''}
              value={templateVersionId}
              onChange={setTemplateVersionId}
            />
            <div className="flex gap-3">
              <button onClick={handlePrevStep} className="btn-secondary flex-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 19.5-7.5-7.5 7.5-7.5" />
                </svg>
                Back
              </button>
              <button
                onClick={handleNextStep}
                disabled={!isStep2Complete}
                className="btn-primary flex-1"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <div className="clinical-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-sm font-semibold text-clinical-charcoal">
                    Real-time translation review
                  </h3>
                  <p className="text-xs font-body text-clinical-text-muted mt-0.5">
                    When ON, flagged translations are held for your review before playing.
                    When OFF, all translations play immediately. Safety annotations are still saved for note review.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={safetyGateEnabled}
                  onClick={() => setSafetyGateEnabled(!safetyGateEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent cursor-pointer transition-colors duration-200 ${
                    safetyGateEnabled ? 'bg-clinical-teal' : 'bg-clinical-cream-dark'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                      safetyGateEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            <Step3AudioCheck />

            {createError && (
              <div
                role="alert"
                className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
              >
                {createError}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handlePrevStep} className="btn-secondary flex-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 19.5-7.5-7.5 7.5-7.5" />
                </svg>
                Back
              </button>
              <button
                onClick={handleStartSession}
                disabled={!canStartSession || createSessionMutation.isPending}
                className="btn-primary flex-1"
                style={{ minHeight: 'var(--touch-target-critical)' }}
              >
                {createSessionMutation.isPending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Starting...
                  </>
                ) : (
                  'Start Session'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
